// src/components/kuestion/useKuestionApi.js
//
// Single entry point for every /api/kuestion/* call.
//
// The backend contract (round 2) settled three things this file owns:
//   - identity comes from `Authorization: Bearer <token>`, never a user_id payload
//   - failures return { status:"error", reason:"<snake_case_code>" }
//   - an expired token is a 401 on every endpoint except exam submit, which the
//     server grants a 3-hour grace on so a finished sitting is never lost
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStudentAuth } from '../../context/StudentAuthContext';
import { kq } from '../../config/routes';

// The full set of reason codes the backend can return, mapped to user-facing
// copy. Wording lives here, not on the server — that was the point of asking
// for stable machine codes, so copy changes never need a backend deploy.
export const REASON_COPY = {
  invalid_token: 'Your session has expired. Please log in again.',
  missing_template_id: 'No exam template was selected.',
  missing_template_name: 'Give the template a name before saving.',
  missing_file: 'No file was attached.',
  missing_file_ids: 'No files are selected.',
  empty_filename: 'That file has no name. Rename it and try again.',
  file_not_found: 'That file no longer exists.',
  template_not_found: 'That template no longer exists.',
  session_not_found: 'That exam session no longer exists.',
  session_already_submitted: 'This exam has already been submitted.',
  sitting_already_submitted: 'This exam has already been submitted.',
  question_not_in_session: 'That question is not part of this sitting.',
  question_not_in_sitting: 'That question is not part of this sitting.',
  invalid_seq: 'That answer could not be saved. It is kept on this device and sent when you submit.',
  no_banks_selected: 'Select at least one question bank.',
  no_questions_in_banks: 'The selected question banks contain no questions.',
  bank_not_found: 'That question bank no longer exists.',
  missing_exam_code: 'Exam code is required.',
  missing_module: 'Module is required.',
  invalid_question_type: 'Pick a valid question type.',
  invalid_reword_level: 'Pick a valid reword complexity.',
  server_error: 'Server error. Please try again shortly.',
  network_error: "Couldn't reach the server. Check your connection and try again.",
  // The server answers this for any path it has not deployed yet.
  route_not_found: 'This part of Kuestion is not switched on at the server yet.',
  method_not_allowed: 'The server does not accept this action yet.',

  // ── Campus v3 (contract §9) ──
  mode_already_set: 'This account’s mode was already chosen and cannot be changed. Contact support if you need a different one.',
  mode_not_set: 'Choose how you will use Kuestion first.',
  prefix_generation_failed: 'We could not generate a free prefix. Please report this.',
  invalid_is_active: 'Active must be on or off.',
  validation_failed: 'Some rows are invalid. Nothing was imported.',
  empty_csv: 'The file has no data rows.',
  invalid_encoding: 'The file is not valid UTF-8.',
  too_many_rows: 'A single import is limited to 1000 rows.',
  duplicate_student_code_in_file: 'Duplicate code within the file',
  sitting_expired: 'The time limit ran out, so this sitting was submitted with the answers you had given.',
  report_not_ready: 'The report is still being built.',
  report_failed: 'The report could not be built. Try again.',
  job_not_found: 'That report job no longer exists.',
  not_campus_account: 'This is a Solo account. Only a Campus account can create an organisation.',
  org_already_exists: 'You already have an organisation.',
  org_not_found: 'You have not created an organisation yet.',
  missing_display_name: 'Give the organisation a name.',
  invalid_display_name: 'That name cannot be used. Try a different one.',
  duplicate_student_code: 'That student code is already used in your organisation.',
  invalid_student_code: 'A student code is exactly 6 letters or numbers.',
  missing_first_name: 'First name is required.',
  missing_last_name: 'Last name is required.',
  student_not_found: 'That student no longer exists.',
  password_too_short: 'Password must be at least 12 characters.',
  password_too_long: 'Password must be 20 characters or fewer.',
  password_needs_upper: 'Password needs an uppercase letter.',
  password_needs_uppercase: 'Password needs an uppercase letter.',
  password_needs_lower: 'Password needs a lowercase letter.',
  password_needs_lowercase: 'Password needs a lowercase letter.',
  password_needs_number: 'Password needs a number.',
  password_needs_special: 'Password needs a special character.',
  invalid_credentials: 'Invalid login ID or password.',
  invalid_login_format: 'A student login looks like RASTER-A7K2M9.',
  invalid_csv: 'That file could not be read as a CSV.',
  invalid_rows: 'Some rows are invalid. Nothing was imported.',
  assignment_not_found: 'That assignment no longer exists.',
  not_assigned_to_you: 'This exam is not assigned to you.',
  assignment_not_open_yet: 'This exam has not opened yet.',
  assignment_not_open: 'This exam has not opened yet.',
  assignment_cancelled: 'Your teacher cancelled this exam before you started it.',
  sitting_not_found: 'That sitting no longer exists.',
  assignment_closed: 'This exam has closed.',
  already_submitted: 'This exam has already been submitted. Proctored exams cannot be retaken.',
  // Shared by PUT /campus/mode (solo | campus) and assignments (proctored | self-assessment).
  invalid_mode: 'That mode is not valid.',
  invalid_window: 'The closing time must be after the opening time.',
  no_students_selected: 'Select at least one student.',
  // as the live assignments route names them (seen 17 Sep)
  no_students: 'Select at least one student.',
  invalid_title: 'Give the assignment a title.',
  invalid_pass_percentage: 'The pass mark must be a whole number between 0 and 100.',
  report_building: 'The PDF is still being built.',
};

export const copyForReason = (reason, fallback = 'Something went wrong. Please try again.') =>
  REASON_COPY[reason] || fallback;

export class KuestionError extends Error {
  constructor(reason, status) {
    super(copyForReason(reason));
    this.name = 'KuestionError';
    this.reason = reason;
    this.status = status;
  }
}

// The one fetch wrapper every Kuestion call goes through. Shared by the
// examiner hook (user token) and the student hook (student token) so the
// envelope, the 401 handling and the network-error translation never drift.
export async function kuestionFetch(url, options, { token, onUnauthorized }) {
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  // fetch() rejects with a bare TypeError when the request never leaves the
  // browser — offline, DNS failure, or a CORS rule that blocks the method.
  // Its message ("Failed to fetch") is meaningless to a user, so translate it.
  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch {
    throw new KuestionError('network_error', 0);
  }

  // A 401 means the token is gone or expired; there is nothing to retry.
  if (res.status === 401) {
    if (onUnauthorized) onUnauthorized();
    throw new KuestionError('invalid_token', 401);
  }

  let json = null;
  try {
    json = await res.json();
  } catch {
    // response may not be JSON; fall through to the error below
  }

  if (!res.ok || !json || json.status !== 'success') {
    const err = new KuestionError(json?.reason || 'server_error', res.status);
    // Bulk import returns per-row errors alongside the reason; keep the body
    // so the screen can show which rows failed.
    if (json) err.body = json;
    throw err;
  }

  return json.data;
}

// Builds the get/post/put/del set over one token. Both hooks below memoise
// the result: callers put `api` in the dependency array of the useCallback
// that loads their data, and that callback drives a mount effect — a fresh
// object each render would refetch forever.
function buildApi(request, token) {
  const get = (url) => request(url, {});
  const postJson = (url, body) => request(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // PUT, not PATCH: the server's Access-Control-Allow-Methods lists
  // GET,POST,PUT,DELETE,OPTIONS — a PATCH is blocked at preflight and never
  // sent. We always send the complete field set, so replacement is accurate.
  const putJson = (url, body) => request(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // Never set Content-Type for FormData — the browser must add the multipart
  // boundary itself.
  const postForm = (url, formData) => request(url, { method: 'POST', body: formData });
  const del = (url) => request(url, { method: 'DELETE' });
  return { get, postJson, putJson, postForm, del, token, isReady: Boolean(token) };
}

export function useKuestionApi() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const token = user?.token;

  // Backend confirmed the token is nested inside the user object, which is
  // where AuthContext already keeps it — data.user.token, not data.token.
  const request = useCallback((url, options = {}) => kuestionFetch(url, options, {
    token,
    onUnauthorized: () => { logout(); navigate(kq('login'), { replace: true }); },
  }), [token, logout, navigate]);

  return useMemo(() => buildApi(request, token), [request, token]);
}

// Same surface, student token. Student endpoints refuse a user token and vice
// versa (separate JWT audience), so the two hooks never share a session.
export function useStudentApi() {
  const { student, logout } = useStudentAuth();
  const navigate = useNavigate();
  const token = student?.token;

  const request = useCallback((url, options = {}) => kuestionFetch(url, options, {
    token,
    onUnauthorized: () => { logout(); navigate(kq('login'), { replace: true }); },
  }), [token, logout, navigate]);

  return useMemo(() => buildApi(request, token), [request, token]);
}

